<template>
  <div class="d-flex flex-column flex-column-fluid">
    <!-- شريط الأدوات -->
    <div id="kt_app_toolbar" class="app-toolbar py-3 py-lg-6">
      <div
        id="kt_app_toolbar_container"
        class="app-container container-fluid d-flex flex-stack"
      >
        <div
          class="page-title d-flex flex-column justify-content-center flex-wrap me-3"
        >
          <h1 class="page-heading text-dark fw-bold fs-3 my-0">
            <i class="fa fa-heart text-danger me-2"></i>
            {{ $t("favorites.events_title") || "الفعاليات المفضلة" }}
          </h1>
          <ul
            class="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1"
          >
            <li class="breadcrumb-item text-muted">
              <router-link to="/" class="text-muted text-hover-primary">
                {{ $t("common.home") || "الصفحة الرئيسية" }}
              </router-link>
            </li>
            <li class="breadcrumb-item">
              <span class="bullet bg-gray-400 w-5px h-2px"></span>
            </li>
            <li class="breadcrumb-item text-muted">
              <router-link to="/events-all" class="text-muted text-hover-primary">
                {{ $t("favorites.events") || "الفعاليات" }}
              </router-link>
            </li>
            <li class="breadcrumb-item">
              <span class="bullet bg-gray-400 w-5px h-2px"></span>
            </li>
            <li class="breadcrumb-item text-muted">
              {{ $t("favorites.title") || "المفضلة" }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- المحتوى -->
    <div id="kt_app_content" class="app-content flex-column-fluid">
      <div id="kt_app_content_container" class="app-container container-fluid">
        <!-- تحميل -->
        <div v-if="isLoading" class="favorites-loading">
          <div class="spinner-border text-success" role="status"></div>
          <span>{{ $t("common.loading") || "جاري التحميل..." }}</span>
        </div>

        <!-- خطأ -->
        <div v-else-if="error" class="favorites-error">
          <i class="fa fa-exclamation-triangle"></i>
          <span>{{ error }}</span>
          <button class="btn btn-outline-success btn-sm mt-2" @click="fetchFavorites">
            {{ $t("common.retry") || "إعادة المحاولة" }}
          </button>
        </div>

        <!-- الفعاليات المفضلة -->
        <div v-else>
          <div v-if="events.length" class="row g-4">
            <div
              v-for="event in events"
              :key="event.id"
              class="col-md-6 col-lg-4 col-xl-3"
            >
              <div
                class="card favorites-card h-100 shadow-sm"
                @click="goToEvent(event.id)"
              >
                <!-- زر إزالة من المفضلة -->
                <button
                  class="favorites-card__remove"
                  @click.stop="removeFromFavorites(event.id)"
                  :title="$t('favorites.remove') || 'إزالة من المفضلة'"
                >
                  <i class="fa fa-heart"></i>
                </button>

                <img
                  :src="getEventImage(event)"
                  class="card-img-top favorites-card__img"
                  alt="صورة الفعالية"
                  @error="handleImageError"
                />
                <div class="card-body">
                  <h5 class="card-title fw-bold text-dark mb-2">
                    {{ event.name_ar || event.name || event.title }}
                  </h5>

                  <!-- المنظم -->
                  <p v-if="event.organizer" class="card-text text-muted small mb-1">
                    <i class="fa fa-building text-success me-1"></i>
                    {{ event.organizer }}
                  </p>

                  <!-- الموقع -->
                  <p v-if="event.location || event.city" class="card-text text-muted small mb-1">
                    <i class="fa fa-map-marker-alt text-success me-1"></i>
                    {{ event.location || event.city }}
                  </p>

                  <!-- التاريخ -->
                  <div class="d-flex flex-column gap-1 small text-muted">
                    <div v-if="event.start_date || event.date">
                      <i class="fa fa-calendar text-success me-1"></i>
                      {{ $t("events.date") || "التاريخ" }}:
                      <span class="fw-semibold">{{ formatDate(event.start_date || event.date) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- لا توجد فعاليات مفضلة -->
          <div v-else class="favorites-empty">
            <i class="fa fa-calendar-alt"></i>
            <h4>{{ $t("favorites.no_events") || "لا توجد فعاليات مفضلة" }}</h4>
            <p>{{ $t("favorites.no_events_desc") || "ابدأ بإضافة الفعاليات التي تهمك إلى المفضلة" }}</p>
            <router-link to="/events-all" class="btn btn-success">
              <i class="fa fa-search me-2"></i>
              {{ $t("favorites.browse_events") || "تصفح الفعاليات" }}
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiUrl } from "@/utils/api";

export default {
  name: "EventsFavorites",
  data() {
    return {
      events: [],
      isLoading: true,
      error: null,
      defaultImage: "/assets/img/acad-icon.png",
    };
  },
  methods: {
    async fetchFavorites() {
      this.isLoading = true;
      this.error = null;
      const token = localStorage.getItem("access_token");

      if (!token) {
        this.error = this.$t("favorites.login_required") || "يجب تسجيل الدخول لعرض المفضلة";
        this.isLoading = false;
        return;
      }

      try {
        const headers = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        };

        const response = await fetch(`${apiUrl}/favorites/events`, { headers });
        const data = await response.json();
        console.log("[EventsFavorites] Response:", data);
        this.events = data.data || data.events || [];
      } catch (err) {
        console.error("[EventsFavorites] Error:", err);
        this.error = this.$t("favorites.fetch_error") || "حدث خطأ في تحميل المفضلة";
      } finally {
        this.isLoading = false;
      }
    },

    async removeFromFavorites(id) {
      const token = localStorage.getItem("access_token");
      if (!token) return;

      try {
        const res = await fetch(`${apiUrl}/favorites/events/${id}`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        
        if (res.ok) {
          this.events = this.events.filter((e) => e.id !== id);
        }
      } catch (err) {
        console.error("[EventsFavorites] Error removing:", err);
      }
    },

    formatDate(iso) {
      try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return "";
        return d.toLocaleDateString("ar-LY");
      } catch (e) {
        return "";
      }
    },

    getEventImage(event) {
      if (event.image) {
        return event.image.startsWith("http")
          ? event.image
          : `${apiUrl}${event.image}`;
      }
      if (event.banner) {
        return event.banner.startsWith("http")
          ? event.banner
          : `${apiUrl}${event.banner}`;
      }
      return this.defaultImage;
    },

    handleImageError(e) {
      e.target.src = this.defaultImage;
    },

    goToEvent(id) {
      this.$router.push(`/event-details?id=${id}`);
    },
  },
  mounted() {
    this.fetchFavorites();
  },
};
</script>

<style scoped>
/* ========== Loading ========== */
.favorites-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 60px 20px;
  color: #666;
  font-size: 1rem;
}

/* ========== Error ========== */
.favorites-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 20px;
  color: #dc3545;
  text-align: center;
}

.favorites-error i {
  font-size: 3rem;
  opacity: 0.5;
}

/* ========== Empty State ========== */
.favorites-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 80px 20px;
  text-align: center;
}

.favorites-empty i {
  font-size: 4rem;
  color: rgba(0, 196, 68, 0.3);
}

.favorites-empty h4 {
  color: #333;
  font-weight: 700;
  margin: 0;
}

.favorites-empty p {
  color: #666;
  margin: 0;
  max-width: 300px;
}

/* ========== Card Styles ========== */
.favorites-card {
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border-radius: 20px !important;
  border: 1px solid rgba(0, 196, 68, 0.08) !important;
  background: linear-gradient(145deg, #ffffff 0%, #fafffe 100%);
  overflow: hidden;
  position: relative;
}

.favorites-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #00c444, #00e050, #00c444);
  background-size: 200% 100%;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 10;
}

.favorites-card:hover::before {
  opacity: 1;
  animation: gradientSlide 2s linear infinite;
}

@keyframes gradientSlide {
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
}

.favorites-card:hover {
  transform: translateY(-12px) scale(1.02);
  box-shadow: 0 25px 50px rgba(0, 196, 68, 0.18), 0 10px 20px rgba(0, 0, 0, 0.08) !important;
  border-color: rgba(0, 196, 68, 0.2) !important;
}

/* ========== Remove Button ========== */
.favorites-card__remove {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 20;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.95);
  color: #dc3545;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.favorites-card__remove:hover {
  background: #dc3545;
  color: #fff;
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(220, 53, 69, 0.4);
}

/* ========== Image Styles ========== */
.favorites-card__img {
  height: 200px;
  object-fit: cover;
  border-radius: 20px 20px 0 0 !important;
  transition: all 0.5s ease;
}

.favorites-card:hover .favorites-card__img {
  transform: scale(1.08);
  filter: brightness(1.05);
}

/* ========== Card Body ========== */
.favorites-card .card-body {
  padding: 20px;
}

.favorites-card .card-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1a1a2e;
  line-height: 1.5;
  margin-bottom: 12px;
  transition: color 0.3s ease;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.favorites-card:hover .card-title {
  color: #00a43c;
}

/* ========== Dark Mode ========== */
[data-bs-theme="dark"] .favorites-card,
[data-theme="dark"] .favorites-card {
  background: linear-gradient(145deg, #242526 0%, #1a1b1d 100%);
  border-color: rgba(0, 196, 68, 0.15) !important;
}

[data-bs-theme="dark"] .favorites-card .card-title,
[data-theme="dark"] .favorites-card .card-title {
  color: #e4e6eb;
}

[data-bs-theme="dark"] .favorites-card:hover .card-title,
[data-theme="dark"] .favorites-card:hover .card-title {
  color: #00e050;
}

[data-bs-theme="dark"] .favorites-empty i,
[data-theme="dark"] .favorites-empty i {
  color: rgba(0, 196, 68, 0.4);
}

[data-bs-theme="dark"] .favorites-empty h4,
[data-theme="dark"] .favorites-empty h4 {
  color: #e4e6eb;
}

[data-bs-theme="dark"] .favorites-empty p,
[data-theme="dark"] .favorites-empty p {
  color: #b0b3b8;
}

[data-bs-theme="dark"] .favorites-card__remove,
[data-theme="dark"] .favorites-card__remove {
  background: rgba(36, 37, 38, 0.95);
}
</style>
